import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  Tooltip,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  FormControl,
  IconButton,
  Select,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  Storefront as StorefrontIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { PageHeader } from '@/components/ui';
import type { PageHeaderAction } from '@/components/ui/PageHeader';
import { useCommonBreadcrumbs } from '@/utils/breadcrumbs';
import { getBreadcrumbConfig } from '@/config/breadcrumbs';
import { toast } from 'react-hot-toast';
import { merchantsService, type Merchant } from '../../../../services/merchantsService';
import { useAuth } from '@/contexts/AuthContext';
import { useSuperadminAuth} from '@/superadmin/contexts/SuperadminAuthContext';

// Import your KYC Drawer component
import KycDrawer from '@/superadmin/pages/merchantList/components/KycDrawer';

export const SuperadminMerchantListPage: React.FC = () => {
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<{ name?: string; email?: string }>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalCount, setTotalCount] = useState(0);

  const [searchKey, setSearchKey] = useState<string>('name');
  const [searchValue, setSearchValue] = useState<string>('');

  // KYC Drawer state
  const [selectedMerchantId, setSelectedMerchantId] = useState<string | number | null>(null);
  const [isKycDrawerOpen, setIsKycDrawerOpen] = useState(false);

  const breadcrumbConfig = getBreadcrumbConfig('superadmin');
  const finalBreadcrumbs = useCommonBreadcrumbs({
    hideBreadcrumbs: breadcrumbConfig.hideBreadcrumbs,
  });

  const { user } = useSuperadminAuth();
  
    if (!user) return <div>No Access</div>;
  
    const hasPermission = (permission: string) => {
      if (user.is_superuser) return true; // superuser has everything
      return user.permission_list?.includes(permission);
    };

  const fetchMerchants = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await merchantsService.getMerchants(
        currentPage,
        pageSize,
        undefined,
        appliedFilters.name,
        appliedFilters.email
      );
      setMerchants(response.results || []);
      setTotalCount(response.count ?? response.results?.length ?? 0);
    } catch (error) {
      console.error('Error loading merchants:', error);
      toast.error('Failed to load merchants');
      setMerchants([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, appliedFilters]);

  useEffect(() => {
    fetchMerchants();
  }, [fetchMerchants]);

  const subtitle = useMemo(
    () => `${totalCount} ${totalCount === 1 ? 'merchant' : 'merchants'}`,
    [totalCount]
  );

  const handleSearch = () => {
    const newFilters = {
      [searchKey]: searchValue.trim() || undefined,
    };
    if (appliedFilters?.[searchKey] === newFilters[searchKey]) {
      fetchMerchants();
      return;
    }
    setAppliedFilters(prev => ({ ...prev, ...newFilters }));
    setCurrentPage(1);
  };

  // const headerActions: PageHeaderAction[] = useMemo(() => [
  //   {
  //     label: '',
  //     icon: <RefreshIcon />,
  //     onClick: () => {
  //       handleSearch();
  //       fetchMerchants();
  //     },
  //     variant: 'text',
  //     size: 'small',
  //   },
  // ], [handleSearch, fetchMerchants]);

  const handleClearSearch = () => {
    setAppliedFilters({});
    setSearchValue('');
    setSearchKey('name');
    setCurrentPage(1);
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setCurrentPage(newPage + 1);
  };

  const handlePageSizeChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const { loginAs } = useAuth();
  const handleLoginAs = async (uuid: string, merchantName: string) => {
    try {
      setIsLoading(true);
      /*******We can call the loginAs function here if required*/
      // await loginAs(uuid);
      // toast.success(`Successfully logged in as ${merchantName}`);
      // setTimeout(() => {
      //     // Open merchant dashboard in a new tab with a query parameter to trigger auth restore
      //     const dashboardUrl = '/dashboard';
      //     const urlWithParam = dashboardUrl.includes('?')
      //       ? `${dashboardUrl}&login_as=true`
      //       : `${dashboardUrl}?login_as=true`;
      //     window.open(urlWithParam, '_blank');
      //   }, 100);
      /******************************End************************/

      const response = await merchantsService.loginAs(uuid);

      if (response.success) {
        toast.success(`Successfully logged in as ${merchantName}`);

        // Store user data in localStorage if provided (for merchant auth context)
        if (response.user) {
          localStorage.setItem('auth_user', JSON.stringify(response.user));
        }

        // Small delay to ensure cookie is set before opening new tab
        setTimeout(() => {
          // Open merchant dashboard in a new tab with a query parameter to trigger auth restore
          const dashboardUrl = response.redirect_url || '/dashboard';
          const urlWithParam = dashboardUrl.includes('?')
            ? `${dashboardUrl}&login_as=true`
            : `${dashboardUrl}?login_as=true`;
          window.open(urlWithParam, '_blank');
        }, 100);
      } else {
        toast.error(response.message || 'Failed to login as merchant');
      }
    } catch (error) {
      console.error('Error logging in as merchant:', error);
      toast.error('Failed to login as merchant');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Merchants"
        subtitle={subtitle}
        breadcrumbs={finalBreadcrumbs}
        // actions={headerActions}
        showDivider={false}
      />

      <Box
        mb={3}
        display="flex"
        gap={2}
        alignItems="center"
        flexWrap="wrap"
        justifyContent="space-between"
      >
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 200, background: 'white' }}>
            <Select value={searchKey} onChange={e => setSearchKey(e.target.value)}>
              <MenuItem value="name">Merchant</MenuItem>
              <MenuItem value="email">Email</MenuItem>
            </Select>
          </FormControl>

          <TextField
            variant="outlined"
            type="search"
            placeholder={`Enter ${searchKey.replace(/_/g, ' ')}`}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleSearch();
            }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, maxWidth: 300, '& .MuiOutlinedInput-root': { background: 'white' } }}
          />
          <Button variant="text" size="small" onClick={handleClearSearch}>
            Clear Filters
          </Button>
        </Box>
        <Tooltip title="Refresh">
          <IconButton
            size="small"
            onClick={() => {
              handleSearch();
              fetchMerchants();
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
      {hasPermission('ADMIN_MERCHANT_LIST_VIEW') ? (
      <Paper elevation={1}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Merchant</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="center">Gateway</TableCell>
                <TableCell align="center">Sub-merchant</TableCell>
                <TableCell align="center">KYC Status</TableCell>
                <TableCell>Sub-merchant ID</TableCell>
                <TableCell>Merchant Key</TableCell>
                <TableCell>Merchant Salt</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : merchants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      {appliedFilters ? 'No merchants match your search.' : 'No merchants found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                merchants.map(merchant => (
                  <TableRow key={merchant.uuid} hover>
                    <TableCell>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <StorefrontIcon color="action" sx={{ fontSize: 18 }} />
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            onClick={() => handleLoginAs(merchant.uuid, merchant.name)}
                            sx={{
                              cursor: 'pointer',
                              color: 'primary.main',
                              '&:hover': { textDecoration: 'underline', color: 'primary.dark' },
                            }}
                          >
                            {merchant.name || 'Unnamed Merchant'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{merchant.email || '—'}</TableCell>
                    <TableCell>{merchant.mobile_number || '—'}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={merchant.is_active ? 'Active' : 'Inactive'}
                        color={merchant.is_active ? 'success' : 'default'}
                        size="small"
                        variant={merchant.is_active ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={merchant.is_gateway_active ? 'Enabled' : 'Disabled'}
                        color={merchant.is_gateway_active ? 'success' : 'default'}
                        size="small"
                        variant={merchant.is_gateway_active ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={merchant.is_submerchant ? 'Yes' : 'No'}
                        color={merchant.is_submerchant ? 'primary' : 'default'}
                        size="small"
                        variant={merchant.is_submerchant ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={merchant.kyc_status ? 'Verified' : 'Pending'}
                        color={merchant.kyc_status ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{merchant.submerchant_id || '—'}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-all' }}>
                      {merchant.key || '—'}
                    </TableCell>
                    <TableCell sx={{ wordBreak: 'break-all' }}>
                      {merchant.salt || '—'}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        onClick={() => {
                          setSelectedMerchantId(merchant.uuid);
                          setIsKycDrawerOpen(true);
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 15, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={pageSize}
          page={currentPage - 1}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePageSizeChange}
        />
      </Paper>
      ) : (
        <Typography variant="body1" color="textSecondary">
          You do not have permission to view this page.
        </Typography>
      )}
      {/* KYC Drawer */}
      {selectedMerchantId && (
        <KycDrawer
          merchantId={selectedMerchantId}
          open={isKycDrawerOpen}
          onClose={() => setIsKycDrawerOpen(false)}
        />
      )}
    </Box>
  );
};

export default SuperadminMerchantListPage;
